export type VaccinationStatus = "overdue" | "upcoming" | "current" | "unknown";

export type ApplicationStatus = "overdue" | "scheduled" | "completed";

export type PetRelationshipKind = "likes" | "dislikes" | "neutral";

export type ReservationVaccination = {
  id: string;
  name: string;
  nextDoseDate: string | null;
  status: VaccinationStatus;
};

export type ReservationApplication = {
  id: string;
  name: string;
  origin?: "application" | "plan";
  petId?: string;
  clinicId?: string;
  itemId?: string;
  nextApplicationItemId?: string | null;
  applicationDate: string;
  doseNumber?: number | null;
  batchNumber?: string | null;
  expiryDate?: string | null;
  adverseReaction?: string | null;
  notes?: string | null;
  nextDoseDate: string | null;
  hasNextDose?: boolean;
  appointmentId?: string | null;
  medicalRecordId?: string | null;
  item?: { name?: string | null } | null;
  nextApplicationItem?: { name?: string | null } | null;
  sourceType?: "manual" | "derived_from_application" | null;
  sourcePetApplicationId?: string | null;
  status: ApplicationStatus;
};

const ALERT_COLOR_CLASSES: Record<string, string> = {
  orange: "bg-akuna-orange",
  amber: "bg-akuna-amber",
  blue: "bg-akuna-blue",
  purple: "bg-akuna-purple",
  green: "bg-state-success",
};

function localDay(value: string) {
  const calendarDate = value.match(/^\d{4}-\d{2}-\d{2}/)?.[0];
  return new Date(calendarDate ? `${calendarDate}T12:00:00` : value);
}

export function classifyApplication(
  application: Pick<ReservationApplication, "applicationDate" | "nextDoseDate">,
  now = new Date(),
): ApplicationStatus {
  if (!application.nextDoseDate) return "completed";
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return localDay(application.nextDoseDate) < today ? "overdue" : "scheduled";
}

export function daysOverdue(dueDate: string, now = new Date()) {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12);
  return Math.max(0, Math.floor((today.getTime() - localDay(dueDate).getTime()) / 86_400_000));
}

export function petAlertColorPresentation(color: string | null) {
  const customColor = color && /^#[0-9a-f]{6}$/i.test(color) ? color : undefined;
  return {
    dotClass: customColor ? "" : ALERT_COLOR_CLASSES[color ?? ""] ?? "bg-state-danger",
    customColor,
  };
}

export function normalizeRelationshipPair(firstPetId: string, secondPetId: string) {
  return firstPetId < secondPetId
    ? { petId: firstPetId, relatedPetId: secondPetId }
    : { petId: secondPetId, relatedPetId: firstPetId };
}

export function classifyVaccination(
  { nextDoseDate }: { nextDoseDate: string | null },
  now = new Date(),
): VaccinationStatus {
  if (!nextDoseDate) return "unknown";

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const due = new Date(`${nextDoseDate}T12:00:00`);
  const upcomingLimit = new Date(today);
  upcomingLimit.setDate(today.getDate() + 7);

  if (due < today) return "overdue";
  if (due <= upcomingLimit) return "upcoming";
  return "current";
}

export function splitRelationships(
  rows: Array<{ petName: string; relationship: PetRelationshipKind }>,
) {
  return {
    likes: rows.filter((row) => row.relationship === "likes").map((row) => row.petName),
    dislikes: rows.filter((row) => row.relationship === "dislikes").map((row) => row.petName),
    neutral: rows.filter((row) => row.relationship === "neutral").map((row) => row.petName),
  };
}

export function splitRelationshipRows<
  T extends { relationship: PetRelationshipKind },
>(rows: T[]) {
  return {
    likes: rows.filter((row) => row.relationship === "likes"),
    dislikes: rows.filter((row) => row.relationship === "dislikes"),
    neutral: rows.filter((row) => row.relationship === "neutral"),
  };
}
